import React, { useState, useMemo, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import ReactApexChart from 'react-apexcharts';
import { Icon } from 'semantic-ui-react';
import { shallowEqual, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { processChartData, getQuestionTypeLabel } from "./dataTransformers";
import Comparisons from "./Comparisons";

// Keyframe Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

// Styled Components
const Container = styled.div`
  background: white;
  font-family: "Raleway", sans-serif;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 100px;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 20px 0;
  background: white;
  position: sticky;
  top: 60px; /* Position below breadcrumb (~50px height + margin) */
  right: 0;
  width: 100%;
  z-index: 200; /* Lower than breadcrumb (300) but higher than content */
  top: 25px;
`;

const ModalButton = styled.button`
  background: #2d70e2;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s ease;
  position: relative;
  z-index: 250;
  &:hover {
    background: #2254aa;
  }
`;

const QuestionSection = styled.div`
  margin: 20px 0 40px 0; /* Increased to account for breadcrumb + header */
  animation: ${slideInUp} 0.8s ease-out;
`;

const QuestionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  position: relative;
`;

const NavigationArrow = styled.button`
  background: none;
  border: none;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #666d79;
  transition: all 0.3s ease;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 101;

  &:hover {
    color: #2a3039;
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const QuestionContent = styled.div`
  flex: 1;
  text-align: center;
  padding: 0 20px;
`;

const QuestionText = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #2a3039;
  margin-bottom: 8px;
  line-height: 1.4;
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;
  transition: all 0.4s ease;
  opacity: 1;
  transform: translateX(0);
`;

const QuestionCategory = styled.p`
  font-size: 14px;
  color: #666d79;
  margin: 0;
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out 0.4s both;
  transition: all 0.4s ease;
  opacity: 1;
  transform: translateX(0);
`;

const DataSection = styled.div`
  margin-top: 32px;
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;
`;

const DataTabs = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 32px;
  justify-content: flex-start;
`;

const Tab = styled.button`
  background: none;
  border: none;
  border-bottom: 2px solid
    ${(props) => (props.active ? "#2D70E2" : "transparent")};
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.active ? "#2D70E2" : "#666D79")};
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0 8px;

  &:hover {
    color: #2d70e2;
    border-bottom-color: ${(props) => (props.active ? "#2D70E2" : "#E1E4E8")};
  }
`;

const ChartContainer = styled.div`
  margin-bottom: 24px;
  animation: ${scaleIn} 0.6s ease-out 0.3s both;
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2a3039;
  margin-bottom: 8px;
`;

const ChartDescription = styled.p`
  font-size: 14px;
  color: #666d79;
  margin-bottom: 24px;
`;

const AverageScore = styled.div`
  text-align: center;
  margin-bottom: 24px;
  font-size: 14px;
  color: #666d79;
`;

const AverageLabel = styled.span`
  font-weight: 600;
  color: #2a3039;
`;

const AverageValue = styled.span`
  font-weight: 600;
  color: #2a3039;
`;

const ResponseBlocks = styled.div`
  display: grid;
  gap: 20px;
  animation: ${fadeInUp} 0.6s ease-out 0.3s both;
`;

const ResponseBlock = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid #f2f2f2;
  transition: all 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    transform: translateX(4px);
  }
`;

const ResponseLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2a3039;
  margin-bottom: 8px;
`;

const ResponseCount = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #2a3039;
  margin-bottom: 4px;
`;

const ResponsePercentage = styled.div`
  font-size: 12px;
  color: #666d79;
`;

const ThemeBlock = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid #f2f2f2;

  &:last-child {
    border-bottom: none;
  }
`;

const ThemeName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2a3039;
  margin-bottom: 4px;
`;

const ThemeStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ThemeCount = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2a3039;
`;

const ThemePercentage = styled.div`
  font-size: 12px;
  color: #666d79;
`;

// Modal Components
const modalOpenAnimation = keyframes`
  from {
    transform: translate(-50%, -50%) scale(0.5);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
`;

const ModalBackdrop = styled.div`
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow: hidden;
  animation: ${modalOpenAnimation} 0.3s ease-out;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e1e4e8;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #2a3039;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #666d79;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #2a3039;
  }
`;

const ModalTabs = styled.div`
  display: flex;
  border-bottom: 1px solid #e1e4e8;
`;

const ModalTab = styled.button`
  background: none;
  border: none;
  padding: 16px 24px;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.active ? "#2D70E2" : "#666D79")};
  border-bottom: 2px solid
    ${(props) => (props.active ? "#2D70E2" : "transparent")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #2d70e2;
  }
`;

const ModalContent = styled.div`
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
`;

const QuestionItem = styled.div`
  padding: 16px;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.selected ? "#F8FAFF" : "white")};
  border-color: ${(props) => (props.selected ? "#2D70E2" : "#E1E4E8")};

  &:hover {
    border-color: #2d70e2;
    background: #f8faff;
  }
`;

const QuestionItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const QuestionItemTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2a3039;
  flex: 1;
  margin-right: 16px;
`;

const QuestionType = styled.span`
  background: #ebf1fd;
  color: #2d70e2;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
`;

const RespondentItem = styled.div`
  padding: 16px;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.selected ? "#F8FAFF" : "white")};
  border-color: ${(props) => (props.selected ? "#2D70E2" : "#E1E4E8")};

  &:hover {
    border-color: #2d70e2;
    background: #f8faff;
  }
`;

const RespondentName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2a3039;
  margin-bottom: 4px;
`;

const RespondentDepartment = styled.div`
  font-size: 12px;
  color: #666d79;
`;

// Respondent View Components
const RespondentView = styled.div`
  margin-top: 32px;
  animation: ${slideInUp} 0.8s ease-out;
`;

const RespondentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  position: relative;
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;
`;

const RespondentInfo = styled.div``;

const RespondentNameLarge = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #2a3039;
  margin-bottom: 4px;
  transition: all 0.4s ease;
  opacity: 1;
  transform: translateX(0);
`;

const RespondentDepartmentLarge = styled.div`
  font-size: 14px;
  color: #666d79;
  transition: all 0.4s ease;
  opacity: 1;
  transform: translateX(0);
`;

const AllResponsesContainer = styled.div`
  display: grid;
  gap: 24px;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;
`;

const ResponseCard = styled.div`
  background: white;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  padding: 20px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ResponseQuestion = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2a3039;
  margin-bottom: 12px;
`;

const ResponseAnswer = styled.div`
  font-size: 14px;
  color: #666d79;
  line-height: 1.5;
`;

const OpenEndedResponse = styled.div`
  font-size: 16px;
  color: #2a3039;
  line-height: 1.5;
  padding: 8px 0;
`;

const FollowUpContainer = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #f8faff;
  border-left: 4px solid #2d70e2;
  border-radius: 0 6px 6px 0;
`;

const FollowUpLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2d70e2;
  margin-bottom: 8px;
`;

const FollowUpQuestion = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: #2a3039;
  margin-bottom: 8px;
  font-style: italic;
`;

const FollowUpResponse = styled.div`
  font-size: 15px;
  color: #2a3039;
  line-height: 1.5;
`;

const ResponseScore = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #2d70e2;
  text-align: center;
  padding: 16px;
  background: #f8faff;
  border-radius: 6px;
  margin-top: 8px;
`;

const PulseAnalytics = ({
  pulseData = null,
  isAnonymous = false,
  orgRule = null,
  surveyInfo = {},
  onModalOpen,
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const is360Review = searchParams.get('360_review') === 'true';

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentView, setCurrentView] = useState("aggregate"); // 'aggregate' or 'respondent'
  const [currentRespondentIndex, setCurrentRespondentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeDataTab, setActiveDataTab] = useState("distribution");

  // Store modal opening function reference
  useEffect(() => {
    if (onModalOpen && typeof onModalOpen === "function") {
      onModalOpen(() => setModalOpen(true));
    }
  }, []);
  const [modalTab, setModalTab] = useState("questions"); // 'questions' or 'respondents'
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [selectedRespondentId, setSelectedRespondentId] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayQuestionIndex, setDisplayQuestionIndex] = useState(0);
  const [displayRespondentIndex, setDisplayRespondentIndex] = useState(0);

  const { get_employee_org_category } = useSelector(
    (state) => ({
      get_employee_org_category: state.employee_category.employee_org_category,
    }),
    shallowEqual
  );

  // Helper function to get user category by employee ID
  const getUserCategory = (employeeId) => {
    // If no context or userList is available, return default
    if (!get_employee_org_category || get_employee_org_category.length === 0) {
      return "Unknown Category";
    }

    const user = get_employee_org_category.find(
      (user) => user.employee_id === employeeId || user.employee === employeeId
    );

    const category = user?.category?.find(
      (cat) => cat?.priority === "secondary"
    )?.value;

    return category || "Unknown Category";
  };

  // Enhance provided data with user categories from context
  const dataSource = useMemo(() => {
    if (!pulseData) {
      console.log('PulseAnalytics: pulseData is null/undefined', { pulseData, is360Review });
      return pulseData;
    }

    return {
      ...pulseData,
      respondents: pulseData.respondents?.map((respondent) => {
        const derivedCategory = getUserCategory(respondent.id);
        const fallbackDepartment =
          typeof respondent?.department === "string" &&
            respondent.department.trim() !== ""
            ? respondent.department
            : null;

        return {
          ...respondent,
          department:
            derivedCategory && derivedCategory !== "Unknown Category"
              ? derivedCategory
              : fallbackDepartment || "Unknown Category",
        };
      }),
    };
  }, [pulseData, get_employee_org_category]);

  const respondents = dataSource?.respondents || [];

  const employeeCategoryIndex = useMemo(() => {
    const map = new Map();

    if (!Array.isArray(get_employee_org_category)) {
      return map;
    }

    get_employee_org_category.forEach((entry) => {
      if (!entry) return;

      const employeeId =
        entry?.employee_id ?? entry?.employee ?? entry?.id;

      if (employeeId === undefined || employeeId === null) {
        return;
      }

      const categories = Array.isArray(entry.category)
        ? entry.category
        : [];

      map.set(String(employeeId), categories);
    });

    return map;
  }, [get_employee_org_category]);

  const groupingOptions = useMemo(() => {
    const options = [];
    const categoryDefinitions = new Map();

    employeeCategoryIndex.forEach((categories) => {
      if (!Array.isArray(categories)) return;

      categories.forEach((category) => {
        if (!category) return;

        const identifier =
          category?.name_id ??
          category?.category_id ??
          category?.id ??
          (category?.name ? `name:${category.name}` : null) ??
          (category?.type ? `type:${category.type}` : null);

        if (!identifier) return;

        if (!categoryDefinitions.has(identifier)) {
          const label =
            category?.name ||
            category?.category ||
            category?.type ||
            "Category";

          categoryDefinitions.set(identifier, {
            id: `category-${identifier}`,
            label,
            identifier,
          });
        }
      });
    });

    const hasDepartmentData = respondents.some(
      (respondent) =>
        respondent?.department &&
        respondent.department !== "Unknown Category"
    );

    if (hasDepartmentData) {
      options.push({
        id: "department",
        label: "Department",
        getValue: (respondent) => {
          if (
            respondent?.department &&
            respondent.department !== "Unknown Category"
          ) {
            return respondent.department;
          }
          return null;
        },
      });
    }

    categoryDefinitions.forEach(({ id, label, identifier }) => {
      const identifierRef = identifier;
      options.push({
        id,
        label,
        getValue: (respondent) => {
          const respondentCategories =
            employeeCategoryIndex.get(String(respondent?.id)) ||
            employeeCategoryIndex.get(String(respondent?.employee_id)) ||
            employeeCategoryIndex.get(String(respondent?.employee));
          if (!Array.isArray(respondentCategories)) {
            return null;
          }
          const matched = respondentCategories.find((item) => {
            const itemIdentifier =
              item?.name_id ??
              item?.category_id ??
              item?.id ??
              (item?.name ? `name:${item.name}` : null) ??
              (item?.type ? `type:${item.type}` : null);
            return itemIdentifier === identifierRef;
          });
          if (!matched) return null;
          return (
            matched?.value ||
            matched?.text ||
            matched?.label ||
            matched?.name ||
            matched?.value_text ||
            matched?.valueName ||
            null
          );
        },
      });
    });

    return options.filter((option) => {
      if (typeof option.getValue !== "function") {
        return false;
      }

      return respondents.some((respondent) => {
        const value = option.getValue(respondent);
        if (!value) return false;
        const trimmed = String(value).trim();
        return trimmed && trimmed !== "Unknown Category";
      });
    });
  }, [respondents, employeeCategoryIndex]);

  // Check if we should show data for anonymous surveys based on orgRule (minimum data points)
  const shouldShowAnonymousData = useMemo(() => {
    if (!isAnonymous) return true;
    
    // If 360_review flag is set, ignore the threshold and always show data
    if (is360Review) return true;

    // For anonymous surveys, check if we have enough responses based on orgRule
    const totalResponses = dataSource?.respondents?.length || 0;
    const minDataPoints = typeof orgRule === 'number' ? orgRule : 3; // Default to 3 if not a number

    return totalResponses > minDataPoints;
  }, [isAnonymous, dataSource?.respondents?.length, orgRule, is360Review]);

  // For anonymous surveys, never show individual responses
  const canShowIndividualResponses = !isAnonymous;

  // Reset to questions tab and aggregate view for anonymous surveys
  useEffect(() => {
    if (!canShowIndividualResponses) {
      setCurrentView("aggregate");
      setModalTab("questions");
    }
  }, [canShowIndividualResponses]);

  const currentQuestion = dataSource?.questions?.[displayQuestionIndex];
  const currentRespondent = respondents[displayRespondentIndex];

  const chartData = useMemo(() => {
    if (!currentQuestion) return [];
    return processChartData(currentQuestion);
  }, [currentQuestion]);

  const chartOptions = useMemo(() => {
    if (currentQuestion?.type === "quantitative") {
      return {
        chart: {
          type: "bar",
          height: 300,
          fontFamily: "Raleway, sans-serif",
          foreColor: "#666D79",
          toolbar: {
            show: false,
          },
          animations: {
            enabled: false,
          },
        },
        colors: ["#2D70E2"],
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: [4, 4, 0, 0],
            barHeight: "70%",
          },
        },
        legend: {
          show: false,
        },
        fill: {
          type: "solid",
        },
        grid: {
          show: true,
          borderColor: "#E1E4E8",
          strokeDashArray: 3,
          xaxis: {
            lines: { show: true },
          },
          yaxis: {
            lines: { show: false },
          },
        },
        xaxis: {
          categories: chartData.map((item) => item.score),
          title: {
            text: "Score",
            style: {
              color: "#666D79",
              fontSize: "14px",
              fontFamily: "Raleway, sans-serif",
            },
          },
          labels: {
            style: {
              fontFamily: "Raleway, sans-serif",
              fontSize: "12px",
              color: "#666D79",
            },
          },
        },
        yaxis: {
          labels: {
            show: false,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val;
          },
          style: {
            fontSize: "12px",
            fontFamily: "Raleway, sans-serif",
            color: "#2A3039",
          },
        },
        tooltip: {
          enabled: false,
        },
        states: {
          hover: {
            filter: {
              type: "none",
            },
          },
        },
      };
    }
    return {};
  }, [currentQuestion, chartData]);

  const chartSeries = useMemo(() => {
    if (currentQuestion?.type === "quantitative") {
      return [
        {
          name: "Responses",
          data: chartData.map((item) => item.count),
        },
      ];
    }
    return [];
  }, [currentQuestion, chartData]);

  const handleQuestionNavigation = (direction) => {
    if (direction === "prev" && currentQuestionIndex > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
        setDisplayQuestionIndex(currentQuestionIndex - 1);
        setIsTransitioning(false);
      }, 200);
    } else if (
      direction === "next" &&
      currentQuestionIndex < (dataSource?.questions?.length || 0) - 1
    ) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setDisplayQuestionIndex(currentQuestionIndex + 1);
        setIsTransitioning(false);
      }, 200);
    }
  };

  const handleRespondentNavigation = (direction) => {
    if (direction === "prev" && currentRespondentIndex > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentRespondentIndex(currentRespondentIndex - 1);
        setDisplayRespondentIndex(currentRespondentIndex - 1);
        setIsTransitioning(false);
      }, 200);
    } else if (
      direction === "next" &&
      currentRespondentIndex < (dataSource?.respondents?.length || 0) - 1
    ) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentRespondentIndex(currentRespondentIndex + 1);
        setDisplayRespondentIndex(currentRespondentIndex + 1);
        setIsTransitioning(false);
      }, 200);
    }
  };

  const handleQuestionSelect = (questionId) => {
    const questionIndex =
      dataSource?.questions?.findIndex((q) => q.id === questionId) ?? -1;
    if (questionIndex !== -1) {
      setCurrentQuestionIndex(questionIndex);
      setDisplayQuestionIndex(questionIndex);
      setSelectedQuestionId(questionId);
      setModalOpen(false);
    }
  };

  const handleRespondentSelect = (respondentId) => {
    const respondentIndex =
      dataSource?.respondents?.findIndex((r) => r.id === respondentId) ?? -1;
    if (respondentIndex !== -1) {
      setCurrentRespondentIndex(respondentIndex);
      setDisplayRespondentIndex(respondentIndex);
      setSelectedRespondentId(respondentId);
      setCurrentView("respondent");
      setModalOpen(false);
    }
  };

  const handleBackToAggregate = () => {
    setCurrentView("aggregate");
    setSelectedRespondentId(null);
    setDisplayQuestionIndex(currentQuestionIndex);
  };

  const renderAggregateData = () => {
    if (!currentQuestion) return null;

    if (currentQuestion.type === "quantitative") {
      return (
        <ChartContainer>
          <ChartTitle>Response Distribution</ChartTitle>
          <ChartDescription>
            Detailed breakdown showing the frequency of each response value
          </ChartDescription>
          <AverageScore>
            <AverageLabel>Avg: </AverageLabel>
            <AverageValue>{currentQuestion.responses.average}</AverageValue>
          </AverageScore>
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="bar"
            height={300}
          />
        </ChartContainer>
      );
    } else if (currentQuestion.type === "multiselect") {
      return (
        <ResponseBlocks>
          {currentQuestion.responses.options.map((option, index) => (
            <ResponseBlock key={index}>
              <ResponseLabel>{option.label}</ResponseLabel>
              <ResponseCount>{option.count}</ResponseCount>
              <ResponsePercentage>
                {option.percentage}% of responses
              </ResponsePercentage>
            </ResponseBlock>
          ))}
        </ResponseBlocks>
      );
    } else if (currentQuestion.type === "openended") {
      // Show individual responses for open text questions
      const individualResponses =
        dataSource?.respondents
          ?.map((respondent) => {
            const responseData = respondent.responses?.[currentQuestion.id];
            const response =
              typeof responseData === "string"
                ? responseData
                : responseData?.response;
            const followUp =
              typeof responseData === "object" ? responseData?.followUp : null;

            return {
              name: isAnonymous ? "Anonymous Respondent" : respondent.name,
              response: response,
              followUp: followUp,
            };
          })
          ?.filter((item) => item.response && item.response.trim() !== "") ||
        [];

      return (
        <ResponseBlocks>
          {individualResponses.map((item, index) => (
            <ResponseBlock key={index}>
              <ResponseLabel>{item.name}</ResponseLabel>
              <OpenEndedResponse>{item.response}</OpenEndedResponse>
              {item.followUp && (
                <FollowUpContainer>
                  <FollowUpLabel>AI Follow-up:</FollowUpLabel>
                  <FollowUpQuestion>{item.followUp.question}</FollowUpQuestion>
                  <FollowUpResponse>{item.followUp.response}</FollowUpResponse>
                </FollowUpContainer>
              )}
            </ResponseBlock>
          ))}
          {individualResponses.length === 0 && (
            <ResponseBlock>
              <ResponseLabel>No responses available</ResponseLabel>
            </ResponseBlock>
          )}
        </ResponseBlocks>
      );
    }
    return null;
  };

  const renderRespondentView = () => {
    if (!currentRespondent || !dataSource?.questions) return null;

    return (
      <RespondentView>
        <RespondentHeader>
          <RespondentInfo>
            <RespondentNameLarge
              style={{
                opacity: isTransitioning ? 0.3 : 1,
                transform: isTransitioning
                  ? "translateX(-30px)"
                  : "translateX(0)",
              }}
            >
              {currentRespondent.name}
            </RespondentNameLarge>
            <RespondentDepartmentLarge
              style={{
                opacity: isTransitioning ? 0.3 : 1,
                transform: isTransitioning
                  ? "translateX(-20px)"
                  : "translateX(0)",
              }}
            >
              {currentRespondent.department}
            </RespondentDepartmentLarge>
          </RespondentInfo>
          <NavigationArrow
            onClick={() => handleRespondentNavigation("prev")}
            disabled={displayRespondentIndex === 0}
            style={{ left: "-100px" }}
          >
            <Icon name="chevron left" size="large" />
          </NavigationArrow>
          <NavigationArrow
            onClick={() => handleRespondentNavigation("next")}
            disabled={
              displayRespondentIndex ===
              (dataSource?.respondents?.length || 0) - 1
            }
            style={{ right: "-100px" }}
          >
            <Icon name="chevron right" size="large" />
          </NavigationArrow>
        </RespondentHeader>
        <AllResponsesContainer>
          {dataSource?.questions?.map((question) => (
            <ResponseCard key={question.id}>
              <ResponseQuestion>{question.text}</ResponseQuestion>
              {(() => {
                const responseData =
                  currentRespondent?.responses?.[question.id];
                const response =
                  typeof responseData === "string"
                    ? responseData
                    : responseData?.response;
                const followUp =
                  typeof responseData === "object"
                    ? responseData?.followUp
                    : null;

                return (
                  <>
                    {question.type === "quantitative" ? (
                      <ResponseScore>
                        {response}/{question.scaleRange?.max || 5}
                      </ResponseScore>
                    ) : question.type === "multiselect" ? (
                      <ResponseAnswer>{response}</ResponseAnswer>
                    ) : (
                      <ResponseAnswer>{response}</ResponseAnswer>
                    )}
                    {followUp && (
                      <FollowUpContainer>
                        <FollowUpLabel>AI Follow-up:</FollowUpLabel>
                        <FollowUpQuestion>{followUp.question}</FollowUpQuestion>
                        <FollowUpResponse>{followUp.response}</FollowUpResponse>
                      </FollowUpContainer>
                    )}
                  </>
                );
              })()}
            </ResponseCard>
          ))}
        </AllResponsesContainer>
      </RespondentView>
    );
  };


  // Final safety check before rendering
  if (!dataSource?.questions?.length || !shouldShowAnonymousData) {
    return (
      <Container>
        <Header></Header>
        <div
          style={{
            padding: "80px 40px",
            textAlign: "center",
            color: "#666D79",
          }}
        >
          <Icon
            name="chart bar outline"
            size="huge"
            style={{ marginBottom: "24px", color: "#E1E4E8" }}
          />
          <h2
            style={{
              color: "#2A3039",
              fontSize: "24px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            No Survey Data Available
          </h2>
          <p
            style={{
              fontSize: "16px",
              lineHeight: "1.5",
              maxWidth: "400px",
              margin: "0 auto",
            }}
          >
            There is currently no pulse survey data to display. Please check
            back once survey responses have been collected.
          </p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      {/* Back button for respondent view - now shown at top of content */}
      {currentView === "respondent" && canShowIndividualResponses && (
        <div
          style={{
            padding: "20px 0 0 0",
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <ModalButton
            onClick={handleBackToAggregate}
            style={{ background: "#666D79" }}
          >
            ← Back to Aggregate
          </ModalButton>
        </div>
      )}

      {currentView === "aggregate" || !canShowIndividualResponses ? (
        <>
          <QuestionSection>
            <QuestionContainer>
              <NavigationArrow
                onClick={() => handleQuestionNavigation("prev")}
                disabled={displayQuestionIndex === 0}
                style={{ left: "-100px" }}
              >
                <Icon name="chevron left" size="large" />
              </NavigationArrow>
              <QuestionContent>
                <QuestionText
                  style={{
                    opacity: isTransitioning ? 0.3 : 1,
                    transform: isTransitioning
                      ? "translateX(-30px)"
                      : "translateX(0)",
                  }}
                >
                  {currentQuestion.text}
                </QuestionText>
                <QuestionCategory
                  style={{
                    opacity: isTransitioning ? 0.3 : 1,
                    transform: isTransitioning
                      ? "translateX(-20px)"
                      : "translateX(0)",
                  }}
                >
                  {currentQuestion.category}
                </QuestionCategory>
              </QuestionContent>
              <NavigationArrow
                onClick={() => handleQuestionNavigation("next")}
                disabled={
                  displayQuestionIndex ===
                  (dataSource?.questions?.length || 0) - 1
                }
                style={{ right: "-100px" }}
              >
                <Icon name="chevron right" size="large" />
              </NavigationArrow>
            </QuestionContainer>
          </QuestionSection>

          <DataSection>
            <DataTabs>
              <Tab
                active={activeDataTab === "distribution"}
                onClick={() => setActiveDataTab("distribution")}
              >
                Distribution
              </Tab>
              <Tab
                active={activeDataTab === "comparison"}
                onClick={() => setActiveDataTab("comparison")}
              >
                Comparison
              </Tab>
            </DataTabs>
            {activeDataTab === "distribution" ? (
              renderAggregateData()
            ) : (
              <Comparisons
                question={currentQuestion}
                respondents={respondents}
                groupingOptions={groupingOptions}
                orgRule={orgRule}
              />
            )}
          </DataSection>
        </>
      ) : (
        canShowIndividualResponses && renderRespondentView()
      )}

      {/* Modal */}
      <ModalBackdrop isOpen={modalOpen}>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>
              Select {modalTab === "questions" ? "Question" : "Respondent"}
            </ModalTitle>
            <CloseButton onClick={() => setModalOpen(false)}>×</CloseButton>
          </ModalHeader>
          <ModalTabs>
            <ModalTab
              active={modalTab === "questions"}
              onClick={() => setModalTab("questions")}
            >
              Questions
            </ModalTab>
            {canShowIndividualResponses && (
              <ModalTab
                active={modalTab === "respondents"}
                onClick={() => setModalTab("respondents")}
              >
                Respondents
              </ModalTab>
            )}
          </ModalTabs>
          <ModalContent>
            {modalTab === "questions"
              ? dataSource?.questions?.map((question) => (
                  <QuestionItem
                    key={question.id}
                    selected={selectedQuestionId === question.id}
                    onClick={() => handleQuestionSelect(question.id)}
                  >
                    <QuestionItemHeader>
                      <QuestionItemTitle>{question.text}</QuestionItemTitle>
                      <QuestionType>
                        {getQuestionTypeLabel(question.type)}
                      </QuestionType>
                    </QuestionItemHeader>
                  </QuestionItem>
                ))
              : canShowIndividualResponses && modalTab === "respondents"
              ? dataSource?.respondents?.map((respondent) => (
                  <RespondentItem
                    key={respondent.id}
                    selected={selectedRespondentId === respondent.id}
                    onClick={() => handleRespondentSelect(respondent.id)}
                  >
                    <RespondentName>{respondent.name}</RespondentName>
                    <RespondentDepartment>
                      {respondent.department}
                    </RespondentDepartment>
                  </RespondentItem>
                ))
              : null}
          </ModalContent>
        </ModalContainer>
      </ModalBackdrop>
    </Container>
  );
};

export default PulseAnalytics;
